import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { baseUrl, environment } from 'src/environments/environment';

export interface MemberRecord {
  uid?: string;
  invitee_uid?: string;
  companyRoles?: string[];
  invitationRoles?: string[];
  id: number;
  name: string;
  email: string;
  company: string;
  role: string;
  roles: string[];
  permissions: string[];
  badge: string;
  status: 'ACTIVE' | 'INACTIVE';
  joinedAt: string;
  location: string | null;
  kind: 'member';
  contactId: string;
}

export interface InviteRecord {
  invitee_uid?: string;
  id: number;
  name: string;
  first_name?: string;
  last_name?: string;
  email: string;
  company: string;
  role: string;
  roles: string[];
  permissions: string[];
  status: 'pending' | 'accepted' | 'rejected';
  badge: string;
  sentAt: string;
  token: string;
  kind: 'invite';
  contactId: string;
}

export interface TeamPermissionTierDefaults {
  required: string[];
  default: string[];
  optional: string[];
}

export interface TeamPermissionsConfig {
  permissions: string[];
  roles: string[];
  permissionDescriptions: Record<string, string>;
  rolePermissionDefaults: Record<string, TeamPermissionTierDefaults>;
  permissionDisplayNames?: Record<string, string>;
  roleDisplayNames?: Record<string, string>;
  updatedAt?: string | null;
}

interface ApiResponse<T> {
  data: T;
}

interface InvitationsListResponse {
  status: string;
  message: string;
  self_invitations?: unknown[];
  company_invitations?: unknown[];
}

interface CreateInvitationResponse {
  status: string;
  message: string;
  invitationId?: number;
  invitation_id?: number;
}

function safeJsonParse<T>(value: unknown, fallback: T): T {
  if (typeof value !== 'string') return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

function humanizeAuthority(value: string): string {
  const cleaned = value.replace(/[_-]+/g, ' ').trim();
  if (!cleaned) return '';
  return cleaned.replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
}

function deriveNameFromEmail(email: string): string {
  const local = (email || '').split('@')[0] || '';
  return humanizeAuthority(local.replace(/[.+]/g, ' ')) || email;
}

function deriveNamePartsFromEmail(email: string): { first_name: string; last_name: string } {
  const derived = deriveNameFromEmail(email).trim();
  if (!derived) return { first_name: "", last_name: "" };
  const parts = derived.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return { first_name: parts[0], last_name: "" };
  return { first_name: parts[0], last_name: parts.slice(1).join(" ") };
}

function combineName(firstName: unknown, lastName: unknown): string {
  const first = String(firstName || "").trim();
  const last = String(lastName || "").trim();
  return `${first} ${last}`.trim();
}

function normalizeInvitationStatus(status: unknown): InviteRecord['status'] {
  const value = String(status || '').toLowerCase().trim();
  if (value === 'accepted') return 'accepted';
  if (value === 'rejected') return 'rejected';
  return 'pending';
}

function normalizeRoles(value: unknown): string[] {
  const raw = Array.isArray(value) ? value : String(value ?? '').split(/[,&/]+/g);
  const roles: string[] = [];
  for (const item of raw) {
    const role = String(item ?? '').trim();
    if (!role) continue;
    if (!roles.includes(role)) roles.push(role);
  }
  return roles;
}

function rolesToLabel(roles: string[]): string {
  return roles.filter(Boolean).join(', ');
}

function normalizeAuthoritiesToLabels(authorities: unknown): string[] {
  const parsed = Array.isArray(authorities) ? authorities : safeJsonParse<unknown[]>(authorities, []);
  if (!Array.isArray(parsed)) return [];

  const labels: string[] = [];
  for (const item of parsed) {
    const raw = typeof item === 'string' ? item : String((item as any)?.name || (item as any)?.label || (item as any)?.id || '');
    const label = raw.includes('_') || raw.includes('-') ? humanizeAuthority(raw) : raw;
    if (label && !labels.includes(label)) labels.push(label);
  }
  return labels;
}

@Injectable({ providedIn: 'root' })
export class TeamApiService {
  private baseUrl = baseUrl;

  constructor(private http: HttpClient) {}

  listMembers(id): Observable<MemberRecord[]> {
    return this.http.get<any>(`${this.baseUrl}/members/${id}`).pipe(
      map((res) => {
        const members = (res?.data?.members ?? res?.data ?? res?.members ?? []) as any[];
        if (!Array.isArray(members)) return [];
        return members.map((member) => {
          const roles = normalizeRoles(member?.roles ?? member?.role);
          return {
            ...member,
            role: rolesToLabel(roles) || String(member?.role || "Staff"),
            roles: roles.length ? roles : [String(member?.role || "Staff")],
          } as MemberRecord;
        });
      }),
    );
  }

  updateMember(id: number, payload: Partial<MemberRecord>): Observable<{ status: string; message: string }> {
    const roles = normalizeRoles((payload as any)?.roles ?? payload.role);
    const role = rolesToLabel(roles) || payload.role || '';
    return this.http.put<{ status: string; message: string }>(`${this.baseUrl}/members/${id}`, {
      ...payload,
      role,
      roles,
    });
  }

  listInvitations(id): Observable<InviteRecord[]> {
    return this.http.get<InvitationsListResponse>(`${this.baseUrl}/invitations/${id}`).pipe(
      map((res) => {
        const invites = (res.company_invitations || []) as any[];
        return invites.map((inv) => {
          const email = String(inv?.invitee_email || inv?.email || '').toLowerCase().trim();
          const company = String(inv?.company_name || inv?.company || '');
          const firstName = String(inv?.first_name || inv?.firstName || "").trim();
          const lastName = String(inv?.last_name || inv?.lastName || "").trim();
          const fallback = deriveNamePartsFromEmail(email);
          const effectiveFirstName = firstName || fallback.first_name;
          const effectiveLastName = lastName || fallback.last_name;
          const roles = normalizeRoles(inv?.roles ?? inv?.positions ?? inv?.position ?? inv?.role_position ?? inv?.role ?? "Staff");
          const role = rolesToLabel(roles) || "Staff";
          const permissions = normalizeAuthoritiesToLabels(inv?.authorities ?? inv?.permissions);
          return {
            id: Number(inv?.id),
            invitee_uid: String(inv?.invitee_uid || ""),
            name: combineName(effectiveFirstName, effectiveLastName) || String(inv?.name || deriveNameFromEmail(email)),
            first_name: effectiveFirstName,
            last_name: effectiveLastName,
            email,
            company,
            role,
            roles: roles.length ? roles : ["Staff"],
            permissions,
            status: normalizeInvitationStatus(inv?.status),
            badge: String(inv?.badge || 'Invite'),
            sentAt: String(inv?.sentAt || inv?.created_at || ''),
            token: String(inv?.token || ''),
            kind: 'invite',
            contactId: String(inv?.contactId || inv?.id || ''),
          } as InviteRecord;
        });
      }),
    );
  }

  createInvitation(payload: {
    applicationId: string | number;
    company_name?: string;
    first_name?: string;
    last_name?: string;
    email: string;
    positions?: string[];
    position?: string;
    authorities: unknown[];
  }): Observable<CreateInvitationResponse> {
    const roles = normalizeRoles(payload.positions ?? payload.position ?? "Staff");
    const position = rolesToLabel(roles) || "Staff";
    return this.http.post<CreateInvitationResponse>(`${this.baseUrl}/invitations/create`, {
      ...payload,
      positions: roles,
      position,
    });
  }

  updateInvitation(id: number, payload: Partial<InviteRecord>): Observable<{ status: string; message: string }> {
    const roles = normalizeRoles((payload as any)?.roles ?? payload.role);
    const position = rolesToLabel(roles) || payload.role || '';
    return this.http.post<{ status: string; message: string }>(`${this.baseUrl}/invitations/update`, {
      id,
      positions: roles,
      position,
      authorities: payload.permissions,
    });
  }

  resendInvitation(id: number): Observable<{ status: string; message: string }> {
    return this.http.post<{ status: string; message: string }>(`${this.baseUrl}/invitations/${id}/resend`, {});
  }

  cancelInvitation(id: number): Observable<{ status: string; message: string }> {
    return this.http.delete<{ status: string; message: string }>(`${this.baseUrl}/invitations/${id}`);
  }

  acceptInvitation(token: string): Observable<{ status: string; message: string }> {
    return this.http.put<{ status: string; message: string }>(`${this.baseUrl}/invitations/${token}/accept`, {});
  }

  rejectInvitation(token: string): Observable<{ status: string; message: string }> {
    return this.http.put<{ status: string; message: string }>(`${this.baseUrl}/invitations/${token}/reject`, {});
  }

  getInvitation(token: string): Observable<InviteRecord> {
    return this.http.get<ApiResponse<InviteRecord>>(`${this.baseUrl}/verify-invitations/${token}`).pipe(map((res) => res.data));
  }

  getTeamPermissionsConfig(): Observable<TeamPermissionsConfig> {
    return this.http
      .get<{ config: TeamPermissionsConfig }>(`${this.baseUrl}/team-permissions-config`)
      .pipe(map((res) => res?.config as TeamPermissionsConfig));
  }
}
